/**
 * READY completion screen — the final "you're connected" step of onboarding, per the
 * `sReady` screen in the onboarding prototype
 * (docs/superpowers/design-reference/1815-onboarding/onboarding-prototype.dc.html, lines
 * 404-435): an Eyebrow reading "Ready" with a right-aligned positive "TRUSTED" chip
 * (lines 406-408), the serif heading "You're connected." (line 410), a bordered summary
 * table of DEVICE / SIGNED IN / NODE / SYNC rows (lines 411-428), a primary "Enter Client
 * Node" button (line 429), and an optional, non-blocking "Install the app on this device —
 * optional" text affordance (line 430).
 *
 * Pure presentation: it owns no completion state of its own, it only renders whatever
 * `ReadyState` the kernel-driven flow hands it and emits `enter` / `install` for the host to
 * act on. The install affordance is deliberately a secondary, dismissible control — it must
 * never gate or disable the primary "Enter Client Node" action, since PWA installability is
 * an enhancement, not a requirement to use the app.
 *
 * Mirrors `./identify-identity.ts` and `../../pairing/pairing-view.ts`'s idiom: `extends
 * HTMLElement`, `set state()` -> `#render()`, shadow root + inline `<style>` using
 * `var(--token, <light-literal-fallback>)` so the shell's token sheet (adopted at the
 * `<node-app-shell>` root) drives theming while still standing alone if this element is ever
 * mounted outside that tree, plus the same reduced-motion/forced-colors guards. Not
 * self-registered here (like the mirrored files) — the host and the test file each register
 * the tag with a `customElements.get` guard.
 */
export class NodeOnboardingReadyCompleteElement extends HTMLElement {
    #state = {};
    set state(value) {
        this.#state = value;
        this.#render();
    }
    get state() {
        return this.#state;
    }
    connectedCallback() {
        this.#render();
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        style.textContent = `
      :host { display: block; font-family: var(--ui-font, Inter, system-ui, sans-serif); color: var(--ink-hi, #0A0A0A); }
      section {
        display: flex; flex-direction: column; gap: 16px;
        padding: clamp(20px, 6vw, 36px) clamp(16px, 5vw, 32px);
        background: var(--bg-raised, #FFFFFF);
        border: 1px solid var(--ui-line-soft, rgba(0, 0, 0, 0.06));
        border-radius: var(--ui-radius-lg, 14px);
        box-shadow: var(--ui-shadow-1, 0 1px 1px rgba(10, 10, 10, 0.05), 0 10px 24px -14px rgba(10, 10, 10, 0.25));
      }
      .top-row { display: flex; align-items: center; justify-content: space-between; }
      .eyebrow {
        font: 600 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888);
      }
      .chip {
        font: 600 9.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.1em; text-transform: uppercase;
        padding: 4px 8px; border-radius: 999px;
        color: light-dark(var(--c-green-deep, #2B7258), var(--c-green, #36AA75));
        background: var(--gold-shd, rgba(54, 170, 117, 0.08));
        border: 1px solid var(--c-green, #36AA75);
      }
      h1 {
        margin: 0; font: 400 clamp(1.75rem, 6vw, 2.125rem)/1.05 var(--ui-serif, "Instrument Serif", serif);
      }
      .summary { display: flex; flex-direction: column; border: 1px solid var(--hr, rgba(10, 10, 10, 0.07)); border-radius: var(--ui-radius, 10px); }
      .summary-row {
        display: flex; justify-content: space-between; gap: 12px;
        padding: 12px 14px; border-bottom: 1px solid var(--hr, rgba(10, 10, 10, 0.07));
      }
      .summary-row:last-child { border-bottom: none; }
      .summary-label {
        font: 400 9.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888); padding-top: 2px; flex-shrink: 0;
      }
      .summary-value { font-size: 12.5px; text-align: right; color: var(--ink-hi, #0A0A0A); }
      .enter {
        min-height: 2.5rem; border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px);
        padding: .6rem 1rem; font: 500 .875rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--bg-0, #FFFFFF); background: var(--ink-hi, #0A0A0A); cursor: pointer; width: 100%;
        transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out);
      }
      .enter:hover:not(:disabled) { background: var(--c-coral-deep, #B85551); border-color: var(--c-coral-deep, #B85551); }
      .enter:disabled { opacity: 0.6; cursor: default; }
      .install {
        align-self: flex-start; background: none; border: none; padding: 0; cursor: pointer;
        font: 400 12.5px/1.4 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--ink-low, #888888); text-decoration: underline;
      }
      .enter:focus-visible, .install:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      @media (prefers-reduced-motion: reduce) {
        * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation: none !important; }
      }
      @media (forced-colors: active) {
        section, .summary, .enter { border: 1px solid CanvasText; }
      }
    `;
        const section = document.createElement("section");
        section.setAttribute("aria-live", "polite");
        const topRow = document.createElement("div");
        topRow.className = "top-row";
        const eyebrow = document.createElement("p");
        eyebrow.className = "eyebrow";
        eyebrow.textContent = "Ready";
        const chip = document.createElement("span");
        chip.className = "chip";
        chip.textContent = "TRUSTED";
        topRow.append(eyebrow, chip);
        const heading = document.createElement("h1");
        heading.textContent = "You’re connected.";
        const { deviceName, nodeName, sessionSummary, canInstall = false } = this.#state;
        const summary = document.createElement("div");
        summary.className = "summary";
        const rows = [
            ["DEVICE", deviceName],
            ["NODE", nodeName],
            ["SYNC", sessionSummary],
        ];
        for (const [label, value] of rows) {
            if (value === undefined || value === "")
                continue;
            const row = document.createElement("div");
            row.className = "summary-row";
            const labelSpan = document.createElement("span");
            labelSpan.className = "summary-label";
            labelSpan.textContent = label;
            const valueSpan = document.createElement("span");
            valueSpan.className = "summary-value";
            valueSpan.textContent = value;
            row.append(labelSpan, valueSpan);
            summary.append(row);
        }
        const enterButton = document.createElement("button");
        enterButton.type = "button";
        enterButton.className = "enter";
        enterButton.textContent = "Enter Client Node";
        enterButton.addEventListener("click", () => {
            this.dispatchEvent(new CustomEvent("enter", { bubbles: true, composed: true }));
        });
        section.append(topRow, heading);
        if (summary.childElementCount > 0)
            section.append(summary);
        section.append(enterButton);
        if (canInstall) {
            const installButton = document.createElement("button");
            installButton.type = "button";
            installButton.className = "install";
            installButton.textContent = "Install the app on this device — optional";
            installButton.addEventListener("click", () => {
                this.dispatchEvent(new CustomEvent("install", { bubbles: true, composed: true }));
            });
            section.append(installButton);
        }
        root.append(style, section);
    }
}
//# sourceMappingURL=ready-complete.js.map